import { SUPPORT_MAILTO } from '../config/support';

export type Plan = 'trial' | 'starter' | 'pro' | 'business' | 'enterprise';

export type SubscriptionStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'cancelled'
  | 'paused'
  | 'incomplete';

export interface PlanLimits {
  max_chat_conversations: number;
  max_email_threads: number;
  max_voice_minutes: number;
  max_knowledge_summaries: number;
  max_scrapes: number;
  max_reports: number;
  max_agents: number;
  max_articles: number;
  max_team_members: number;
  max_liveavatar_minutes: number;
  liveavatar_concurrency_cap: number;
  /** Plan-VideoAnalysis.md §B.5 LOCKED — dedicated quota for the
   *  Knowledge Hub "Analyze video" feature. Tier numbers:
   *  Trial 2 / Starter 4 / Pro 10 / Business 30 / Enterprise -1.
   *  Optional in the type — older billing payloads from before Shadow
   *  Fiend's seed migration won't include the field, and we fall back
   *  to a sensible default in the UI. */
  max_video_analyses?: number;
  /** Plan-TextVoicePoolBilling-2026-08.md §3 — the Text pool, in EFFECTIVE
   *  AI tokens per billing cycle (`(input−cached) + 0.1×cached + 6×output`).
   *  `-1` = unlimited.
   *
   *  🚨 The presence of this key does NOT mean the workspace is on pooled
   *  pricing. `main.py` reseeds `db.plan_limits` on every boot and
   *  `api/billing.py` emitted it unconditionally, so it appeared on payloads
   *  from backends with `TEXT_POOL_BILLING_ENABLED` OFF — which rendered a
   *  pooled bar stuck at 0 while hiding the per-category limits that were
   *  actually being enforced. Read `WorkspaceBilling.text_pool_enabled`
   *  instead, via `isPooledBilling()`. */
  max_text_tokens?: number;
}

export interface UsageSnapshot {
  chat_conversations: number;
  email_threads: number;
  voice_minutes: number;
  knowledge_summaries: number;
  web_scrapes: number;
  reports: number;
  liveavatar_minutes?: number;
  agents?: number;
  articles?: number;
  team_members?: number;
  /** Counts toward `max_video_analyses` (Plan-VideoAnalysis.md §B.5). */
  video_analyses?: number;
  /** Plan-StylistUsageMetering.md (2026-06-29) — informational count of
   *  Stylist results produced this cycle. NO cap (no `max_stylist_results`
   *  in PlanLimits) — billing rides `chat_conversations` (+2 per result).
   *  Shopify-only feature, so 0 on Stripe-only workspaces. */
  stylist_results?: number;
  /** Orchestrator messages sent this cycle (informational). Token-metered and
   *  charged to `chat_conversations` — NO separate cap. */
  orchestrator_messages?: number;
  /** Whole chat credits those Orchestrator messages consumed this cycle
   *  (the period total that rode `chat_conversations`). Informational. */
  orchestrator_credits?: number;
  /** Plan-TextVoicePoolBilling-2026-08.md §3 — effective AI tokens drawn
   *  from the Text pool this cycle, across EVERY text surface (chat, email
   *  replies, KB summaries, scrapes, video, Stylist, Orchestrator). Counts
   *  toward `max_text_tokens`. Absent while the backend flag is off. */
  text_tokens?: number;
  /** Phase D — **REMAINING** top-up balance in effective tokens. Durable:
   *  carries forward until spent and NEVER expires (owner decision
   *  2026-08-13).
   *
   *  🚨 THIS IS ITS OWN METER — it is NOT added to `max_text_tokens` any
   *  more (owner 2026-08-17, "isolate the topped up tokens in the display").
   *  A top-up deliberately does not raise the plan limit, so blending it into
   *  the plan bar's denominator meant the plan bar absorbed the purchase and
   *  the customer could never watch the handover. The split is:
   *    plan bar   → `min(text_tokens, max_text_tokens)` / `max_text_tokens`
   *    top-up bar → `max(0, text_tokens - max_text_tokens)` / (that + this)
   *  The two sum to `text_tokens` exactly, so no token is rendered twice.
   *  🚨 Derive BOTH from `deriveTextPool()` in `components/billing/usageBars`
   *  and nowhere else; a second derivation is how the two meters end up
   *  disagreeing. A consequence of the split: the plan bar reading 100% no
   *  longer means the workspace is out of tokens, so no copy may say so
   *  unless this field is also 0.
   *
   *  🚨 THE SEMANTICS FLIPPED, AND THE TWO READINGS FAIL IN OPPOSITE
   *  DIRECTIONS WITH NO ERROR EITHER WAY. It previously meant "GRANTED to
   *  this cycle" (a constant until the cycle rolled); it now means
   *  "REMAINING" (a figure that drains as it is spent). Under the old
   *  reading the denominator was stable; if this ever silently reverts, the
   *  same denominator shrinks as the balance drains and the bar UNDER-reports
   *  usage — the mirror image of the bug the widened denominator fixed. Treat
   *  a change here as a contract change, not a rename.
   *
   *  Absent (stripped) while the backend flag is off. */
  text_tokens_topup?: number;
}

/** Plan-TextVoicePoolBilling-2026-08.md §3 — is this workspace on the
 *  pooled Text+Voice pricing? Read from the billing payload, never from a
 *  frontend env flag.
 *
 *  🚨 The authority is the explicit `text_pool_enabled` boolean, which
 *  mirrors the backend's `TEXT_POOL_BILLING_ENABLED`. The original rule
 *  here — "`max_text_tokens` is present, therefore pooled" — was WRONG and
 *  shipped a live defect: `main.py` reseeds `db.plan_limits` on boot and
 *  `api/billing.py` emitted the key regardless of the flag, so pooled mode
 *  turned itself on against a backend that was still enforcing per-category
 *  limits. The customer saw a Text bar frozen at 0, advertising an
 *  allowance nobody was metering, while the limits actually in force were
 *  hidden. A derived signal was standing in for a fact the server knows;
 *  now the server states it.
 *
 *  The key-presence check survives ONLY as a fallback for payloads predating
 *  the `text_pool_enabled` field, so neither deploy order breaks:
 *    - backend first  → field present → authoritative, either way.
 *    - frontend first → field absent  → falls back → behaves as it does
 *      today, INCLUDING the defect above. That is why this ship must go out
 *      backend-first or simultaneously; the fallback bounds the blast radius
 *      of a mis-ordered deploy, it does not fix it.
 *  Once the backend is deployed everywhere, the fallback is dead code and
 *  should be deleted.
 *
 *  In the fallback branch a literal `0` stays NON-pooled: it is the
 *  unrecognized-plan tier ("no text allowance at all"), which must not flip
 *  the whole billing UI.
 *
 *  Everything the pooled shape replaces (per-category bars, Resource Swap,
 *  Text Bundles, per-category auto-overage, video-quota counters) is HIDDEN
 *  on this one predicate, not deleted — Phase D deletes. */
export const isPooledBilling = (
  billing?: Pick<WorkspaceBilling, 'text_pool_enabled' | 'usage_limits'> | null,
): boolean => {
  if (typeof billing?.text_pool_enabled === 'boolean') {
    return billing.text_pool_enabled;
  }
  const max = billing?.usage_limits?.max_text_tokens;
  return typeof max === 'number' && max !== 0;
};

export interface PendingPlanChange {
  target_plan: Plan;
  target_billing_interval?: 'month' | 'year';
  effective_at: string;
  reason?: 'downgrade' | 'cancel';
}

// ─── Self-serve cancellation (Stripe build only) ──────────────────────
//
// 🚨 Distinct from `PendingPlanChange`. A pending DOWNGRADE keeps the
// workspace on a paid plan; a pending CANCELLATION ends the subscription
// outright at period end. They are mutually exclusive server-side —
// scheduling a cancellation RELEASES any pending plan change, which is why
// `CancelSubscriptionResponse.released_schedule` exists.
export interface PendingCancellation {
  /** When the plan actually ends. Until this moment the workspace keeps its
   *  FULL limits — cancellation is always at period end, never immediate. */
  effective_at: string;
  /** True when an active LiveAvatar add-on will be cancelled alongside the
   *  plan. The add-on cannot outlive the plan it attaches to. Read this on
   *  the BANNER; the pre-confirmation modal has no server answer yet and
   *  reads `liveavatar_addon.status` instead. */
  liveavatar_will_cancel: boolean;
}

export interface CancelSubscriptionResponse {
  status: 'cancel_scheduled';
  effective_at: string;
  /** An active add-on was scheduled to end with the plan. */
  liveavatar_cancelled: boolean;
  /** A pending plan/interval change was destroyed to make room for the
   *  cancellation. It cannot be brought back — resuming restores the plan,
   *  not the scheduled change. */
  released_schedule: boolean;
}

export interface ResumeSubscriptionResponse {
  status: 'resumed';
}

export type LiveAvatarTier = 'starter' | 'growth' | 'scale';

export interface LiveAvatarPendingChange {
  target_tier: LiveAvatarTier | null;
  effective_at: string;
  schedule_id: string;
}

export interface LiveAvatarAddon {
  tier: LiveAvatarTier | null;
  status: 'active' | 'canceled' | null;
  current_period_end: string | null;
  minutes_used: number;
  /** EFFECTIVE tier cap, through the admin-override ladder (`back bc75d88`).
   *  `-1` = unlimited, `0` = no add-on OR an admin hard block.
   *  ⚠️ SEMANTICS CHANGED: this used to be the tier's headline number read
   *  straight from `LIVEAVATAR_TIERS`, so an admin override was invisible here
   *  while the gate enforced it. A genuinely cancelled add-on now reports 0
   *  (cancel-at-period-end is unaffected — it keeps `status: "active"` and only
   *  stamps `pending_change`). */
  minutes_cap: number;
  /** Denominator for the combined minutes bar: effective tier cap PLUS the
   *  purchased minutes available across this cycle. `-1` = unlimited, `0` = no
   *  add-on or hard block. 🚨 ALREADY INCLUDES the bundle — never add to it.
   *
   *  Optional because an older backend does not send it; consumers fall back to
   *  the client-side sum. It exists because the client CANNOT compute it: a
   *  fully drained bundle flips to `status: "exhausted"` and vanishes from
   *  `GET /billing/credits`, so a client sum collapses to the tier cap exactly
   *  when the customer has spent everything. */
  minutes_cap_total?: number;
  /** Whether `buy_liveavatar_bundle` will succeed — the DECISION, from the same
   *  `resolve_liveavatar_entitlement(...).active` the endpoint 403s on, so the
   *  button and the refusal cannot disagree. Optional: older backends omit it.
   *
   *  🚨 Do NOT reconstruct this client-side. The ladder includes a coupon
   *  `current_period_end` deny, and the payload never sends `source` — while for
   *  a Stripe add-on that same field is the RENEWAL date, so treating an elapsed
   *  one as inactive would block every customer on a late renewal webhook. */
  can_buy_bundle?: boolean;
  concurrency_active: number;
  concurrency_cap: number;
  pending_change: LiveAvatarPendingChange | null;
}

export interface LiveAvatarProrationPreview {
  tier: LiveAvatarTier;
  amount_due: number;
  currency: string;
  proration_date: string;
  lines: Array<{ description: string; amount: number }>;
}

export interface LiveAvatarChangeResponse {
  status: 'subscribed' | 'upgraded' | 'scheduled' | 'cancel_scheduled' | 'released';
  tier?: LiveAvatarTier | null;
  effective?: 'immediate';
  effective_at?: string;
  schedule_id?: string;
  message?: string;
}

export interface LiveAvatarTierDisplayEntry {
  key: LiveAvatarTier;
  name: string;
  price: string;
  priceNum: number;
  minutes: number;
  concurrency: number;
  tagline: string;
  perks: string[];
}

export const LIVEAVATAR_TIER_DISPLAY: Record<LiveAvatarTier, LiveAvatarTierDisplayEntry> = {
  starter: {
    key: 'starter',
    name: 'Avatar Starter',
    price: '$29',
    priceNum: 29,
    minutes: 60,
    concurrency: 3,
    tagline: 'Dip your toes in',
    perks: ['60 minutes / month', '3 concurrent sessions', 'HD streaming avatar', 'Standard HeyGen voices'],
  },
  growth: {
    key: 'growth',
    name: 'Avatar Growth',
    price: '$79',
    priceNum: 79,
    minutes: 180,
    concurrency: 6,
    tagline: 'Most popular',
    perks: ['180 minutes / month', '6 concurrent sessions', 'HD streaming avatar', 'Priority session routing'],
  },
  scale: {
    key: 'scale',
    name: 'Avatar Scale',
    price: '$199',
    priceNum: 199,
    minutes: 400,
    concurrency: 12,
    tagline: 'For high-volume teams',
    perks: ['400 minutes / month', '12 concurrent sessions', 'HD streaming avatar', 'Dedicated session pool'],
  },
};

export const LIVEAVATAR_TIER_ORDER: LiveAvatarTier[] = ['starter', 'growth', 'scale'];

export interface InvoiceSummary {
  id: string;
  number?: string;
  amount_due: number;
  amount_paid: number;
  currency: string;
  status: 'paid' | 'open' | 'void' | 'uncollectible' | 'draft';
  created: string;
  hosted_invoice_url?: string;
  invoice_pdf?: string;
  period_start?: string;
  period_end?: string;
}

/** Plan-InvoiceLedgerAndUpcomingChargeEmail-2026-08.md §3 — the PINNED
 *  contract of `GET /api/billing/workspace/{id}/invoices`. This is the
 *  persistent-ledger shape and it is deliberately NOT `InvoiceSummary`:
 *  it covers Shopify + bundle charges too, carries an `upcoming` status
 *  for a charge that has not happened yet, and names its links
 *  `hosted_url` / `pdf_url` (Stripe's payload names them
 *  `hosted_invoice_url` / `invoice_pdf`). `InvoiceSummary` above stays
 *  exactly as it is — it is the legacy `billing.invoices` key that the
 *  pre-flag path still renders. */
export type InvoiceLedgerStatus =
  | 'upcoming'
  | 'paid'
  | 'open'
  | 'failed'
  | 'void'
  | 'refunded';

export interface InvoiceLedgerEntry {
  /**
   * THE row identity, and the only thing safe to use as a React key.
   *
   * 🚨 OPAQUE — never parse it, never assume ObjectId. Ledger-backed rows carry
   * `str(_id)`; live-only rows (history predating the ledger, which under the
   * no-backfill rule will never gain a ledger row) carry `stripe:{invoice_id}`
   * permanently. Both forms are stable and unique, which is all a key needs.
   *
   * Stable across Stripe's list window aging out, too: on a live/ledger overlap
   * the backend keeps the ledger `entry_id` and takes the live values, so a row
   * does not change identity — and therefore does not remount — the day Stripe
   * stops returning it.
   */
  entry_id: string;
  /**
   * `provider_ref`. 🚨 NOT unique per row — a payment that failed and then
   * succeeded produces a `failed` row and a `paid` row carrying the same value.
   * Display/debug only; key on `entry_id`.
   */
  id: string;
  provider: 'stripe' | 'shopify' | 'bundle';
  kind: 'subscription' | 'bundle' | 'addon';
  status: InvoiceLedgerStatus;
  number?: string | null;
  description: string;
  /** 🚨 MINOR units (cents) — always run through `formatCurrency`. */
  amount_due: number;
  amount_paid: number;
  currency: string;
  period_start?: string | null;
  period_end?: string | null;
  created: string;
  hosted_url?: string | null;
  /** Stripe only — null for Shopify/bundle rows. */
  pdf_url?: string | null;
  /**
   * 🚨 A CHARGE NOBODY PAID. Written only by the admin Shopify cycle
   * simulator, which synthesizes the renewal payload LOCALLY and never calls
   * Shopify — so there is no real charge, no Shopify email, and nothing on the
   * merchant's `/admin/charges` page. The row must be badged and its outbound
   * Shopify link suppressed, or it points at a page where the charge does not
   * exist.
   *
   * ⚠️ ABSENT MEANS REAL, and the asymmetry is deliberate: rows written before
   * the field existed carry no key, and mislabelling a genuine payment as a
   * test artifact is the worse error of the two — it invites someone to
   * dismiss money that actually moved. Compare `=== true`, never truthiness.
   *
   * 🚨 No longer staging-only. Production is permitted for Shopify dev stores
   * (`test: true`, verified live at press time), so these rows CAN land in a
   * real merchant workspace's history beside real ones.
   */
  simulated?: boolean;
}

export interface InvoiceLedgerResponse {
  /** `internal` ⇒ `entries: []` and the section stays hidden. */
  provider: 'stripe' | 'shopify' | 'internal';
  entries: InvoiceLedgerEntry[];
  has_more: boolean;
  next_cursor: string | null;
  /** 🚨 THE point of this endpoint: `true` means the live provider call
   *  raised, so the list may be incomplete AND WE KNOW IT. The FE must
   *  never render "No invoices yet." while this is true — that is the
   *  confident-zero bug this ship exists to fix. */
  source_degraded: boolean;
}

export interface WorkspaceBilling {
  workspace_id: string;
  /** Plan-RemoveFreeTrial.md — genuinely `null` for a brand-new / never-paid
   *  workspace (`subscription_status === "incomplete"`, plan not yet chosen).
   *  Callers MUST null-guard before dereferencing `PLAN_DISPLAY[plan]`. Once
   *  the owner picks a plan + pays this is a real `Plan` for the rest of the
   *  workspace's life. */
  plan: Plan | null;
  subscription_status: SubscriptionStatus;
  trial_ends_at?: string | null;
  billing_cycle_start?: string | null;
  billing_cycle_end?: string | null;
  /** Authoritative usage-limit reset date (Storm 4e5c20d). Prefer over
   *  billing_cycle_end (null for trials). Non-null for every real
   *  workspace: Stripe/Shopify → cycle end; trial → trial end;
   *  internal/calendar → first of next month. Null only in a degenerate
   *  no-workspace case → hide the disclosure line. */
  usage_resets_at?: string | null;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  pending_plan_change?: PendingPlanChange | null;
  /** Storm's cancel-subscription contract: ALWAYS present on the payload,
   *  `null` when nothing is scheduled. Non-null hides the "Cancel
   *  subscription" button (the banner replaces it) and drives
   *  PendingCancellationBanner. Optional in the type only so a payload from
   *  a backend predating the field still type-checks — treat absent as
   *  null, never as "unknown". */
  pending_cancellation?: PendingCancellation | null;
  /** 🚨 THE authority on whether cancelling the plan also kills the AI Avatar
   *  add-on — and the ONLY thing the pre-confirm modal may gate that warning
   *  on. Storm derives it from the presence of a real Stripe subscription
   *  ITEM, never from `tier` / `status`.
   *
   *  🚨 DO NOT reconstruct this from `liveavatar_addon`. A COUPON-granted
   *  add-on (`back/utils/trial_grant.py:118`) writes `tier` / `status` /
   *  `current_period_end` with NO `stripe_subscription_item_id` — an
   *  entitlement with no Stripe item behind it, which a plan cancellation
   *  does NOT end. `liveavatar_addon.status === 'active'` is `true` for those
   *  customers, so gating on it tells them their avatar is going away when it
   *  is not. A false warning inside a destructive confirm dialog is worse
   *  than no warning: it teaches people to distrust the next one.
   *  `canUseAvatar` answers "can this workspace use the avatar" — a DIFFERENT
   *  question from "will cancelling destroy it", and only the backend can see
   *  the Stripe item that settles the second one.
   *
   *  Always present post-ship; `false` when no Stripe-item-backed add-on
   *  exists. Optional in the type only for a pre-ship backend, and absent is
   *  read as `false` — the fail-safe direction (a missing warning, never a
   *  false one). That does mean a FE-only deploy would show no avatar
   *  warning at all, which is one more reason for the standing "deploy
   *  `back` first, never `front` alone" rule. */
  liveavatar_will_cancel_on_plan_cancel?: boolean;
  /** When a PROMOTIONAL (coupon-granted) AI Avatar expires, ISO date.
   *  `null` for a genuinely paid add-on and for the Shopify bundled avatar —
   *  render nothing in that case.
   *
   *  🚨 The case this exists for looks completely healthy: the 7-day
   *  marketing trial grants Live Avatar on its OWN 7-day clock, so a customer
   *  who upgrades to a real paid plan on day 3 still loses the avatar on day
   *  7. Nothing else on the page says so — `liveavatar_addon.status` is
   *  `active` and the minutes bar looks normal right up until it dies.
   *
   *  Independent of `liveavatar_will_cancel_on_plan_cancel`: this one expires
   *  on its own schedule REGARDLESS of the plan, which is exactly why
   *  cancelling the plan does not end it. */
  liveavatar_promo_expires_at?: string | null;
  usage_limits: PlanLimits;
  usage: UsageSnapshot;
  /** Plan-TextVoicePoolBilling-2026-08.md §3 — mirrors the backend's
   *  `TEXT_POOL_BILLING_ENABLED` for this workspace. THE authority on
   *  whether pooled Text+Voice pricing is in force; read it through
   *  `isPooledBilling()` rather than directly, so the pre-field fallback
   *  stays in one place. Absent on payloads from a backend predating the
   *  field — see `isPooledBilling()` for why that case is not safe on its
   *  own, and why this ship deploys backend-first. */
  text_pool_enabled?: boolean;
  swap_applied?: Record<string, number>;
  auto_overage?: Record<string, boolean>;
  liveavatar_addon?: LiveAvatarAddon | null;
  invoices?: InvoiceSummary[];
  voice_available?: boolean;
  billing_interval?: BillingInterval;
  /** Workspace is owned/operated by Affirm Digital internally
   *  (`workspace.is_company_owned: true`). Plan-RemoveStripeSandbox §3.1
   *  (2026-05-13): internal workspaces skip Stripe entirely — backend
   *  raises 503 on any subscribe / bundle / metering call. Server-side
   *  usage enforcement is also bypassed (unlimited regardless of plan).
   *  Frontend renders limits + usage exactly like an external workspace
   *  (display-only), but replaces every change-plan CTA with a
   *  "Managed by admin" modal. Backend stamps this flag per workspace
   *  (default false). */
  is_company_owned?: boolean;
  /** Plan-EffectiveLimitsAndMaintenanceBanners.md §1a (2026-05-15) —
   *  canonical resource keys (no `max_` prefix) for which an admin has
   *  set a `workspaces.usage_limit_overrides.<key>` value. The values in
   *  `usage_limits` above are already the EFFECTIVE limits (plan snapshot
   *  merged with override on the backend). Frontend uses this list ONLY
   *  to decide where to render the "Limit set by Prodagi team" italic
   *  disclosure on the Billing page. Empty array (or undefined) when no
   *  overrides are set — disclosure stays hidden. */
  overridden_keys?: string[];
  /** Plan-AutoInstallCommerceAgent §5b/§5e (2026-05-26 — Storm 439407b).
   *  Which billing rail this workspace lives on; gates the Shopify-only
   *  fields below. Stripe-billed workspaces return `"stripe"`,
   *  `shopify_agent_id: null`, and `agents: []`. */
  billing_provider?: 'stripe' | 'shopify';
  /** Plan-SwitchToBillingAPI.md (2026-05-31) — which Shopify billing flow
   *  the backend has wired this build to. `"managed"` (default) keeps the
   *  current Shopify-hosted Managed Pricing page; `"api"` opts in to the
   *  in-app plan picker that calls `appSubscriptionCreate`. Field is
   *  ONLY emitted on Shopify-billed workspaces; absent on Stripe (and on
   *  pre-migration Shopify payloads) → callers default to managed. */
  billing_mode?: 'managed' | 'api';
  /** MongoDB ObjectId-as-string of the agent that owns Shopify for this
   *  workspace (one-workspace-one-agent-one-store). NEVER the URL slug.
   *  Null on Stripe workspaces and on Shopify workspaces that haven't
   *  completed OAuth — branch the Continue-setup CTA off null. */
  shopify_agent_id?: string | null;
  /** Per-agent metadata, ONLY emitted on Shopify-billed workspaces (empty
   *  on stripe). `id` is `str(_id)` (matches `shopify_agent_id`);
   *  `agent_id` is the URL slug; `needs_onboarding=true` force-opens
   *  AgentEditorModal in EDIT mode on Dashboard mount (cleared by the
   *  backend on the next `PUT /api/agents/{slug}` save). */
  agents?: Array<{
    id: string;
    agent_id: string;
    agent_name: string | null;
    needs_onboarding: boolean;
  }>;
  /** Plan-SwitchToBillingAPI follow-up — merchant's `{shop}.myshopify.com`
   *  domain, used by the Billing & Usage page to deep-link the
   *  "Manage plan" button on the current-plan card to the merchant's
   *  Shopify admin charges page (`https://{shop}/admin/charges`) where
   *  they can cancel the subscription. Shopify-only — absent on Stripe
   *  workspaces and on Shopify workspaces that haven't completed OAuth
   *  yet → frontend keeps the button disabled with the legacy
   *  "Current plan" label as a safe fallback. */
  shopify_shop_domain?: string | null;
  /** Plan-IntegrationsBugs-2026-06-09 Bug 3 — true when the workspace has
   *  a stored `shopify_offline_token` (OAuth completed). Shopify-only;
   *  absent on Stripe payloads. Branch AgentShopifyAssignButton's
   *  install flow on this: token absent → open ShopDomainModal + kick
   *  OAuth; token present → pointer-flip only. */
  shopify_offline_token_present?: boolean;
}

// ──────────────────────────────────────────────────────────────────────
// Bundles (Plan-C Hybrid v4 — Plan-BundlesImplementation.md)
// ──────────────────────────────────────────────────────────────────────

/** Per-resource credit currently active on a workspace. Read-only view
 *  shown on the Billing & Usage page "Active credits" panel + the
 *  credit-segment overlay on UsageBarWithCredits.
 *
 *  Wire shape mirrors `back/api/billing_bundles.py:list_active_credits()`
 *  post-Storm Phase 2. The endpoint returns rows sorted by
 *  `expires_at` ASC (oldest-expiring first) — matches the FIFO order
 *  `consume_resource()` reads in.  */
export interface ActiveCredit {
  id: string;
  resource: BundleResource;
  amount: number; // remaining credits
  amount_initial: number; // initial credits at purchase
  unit_price_usd: number;
  total_paid_usd: number;
  bundle_type: BundleType;
  purchased_at: string;
  expires_at: string; // purchased_at + 365 days per §A.5
  stripe_payment_intent?: string;
}

/** `text_topup` is the POOLED-MODE replacement for `text`: one flat SKU,
 *  no allocation. Both members stay in the union permanently — legacy
 *  `text` credit rows keep existing on workspaces that bought the $5
 *  bundle before the pool shipped, and the Active-credits table still has
 *  to label them after the cutover. */
export type BundleType = 'text' | 'voice' | 'liveavatar' | 'text_topup';

/** The resources that can be credited by a bundle. LiveAvatar is its
 *  own resource (minutes); the 5 legacy Text resources are the ones
 *  inside the Text Bundle allocation; `text_tokens` is the single pooled
 *  resource the top-up credits. */
export type BundleResource =
  | 'chat_conversations'
  | 'email_threads'
  | 'knowledge_summaries'
  | 'web_scrapes'
  | 'reports'
  | 'voice_minutes'
  | 'liveavatar_minutes'
  | 'text_tokens';

/** Per-resource Text Bundle rate in TEN-THOUSANDTHS OF A DOLLAR (= 0.01
 *  cents = 1 "tenth-mill"). Integer-only internal math avoids float
 *  drift in the sum-to-$5.00 validation (see plan §F risk #3). The
 *  Text Bundle's target sum is exactly 50000 units = $5.00.
 *
 *  Rates derived from plan §A.2 (v3 per-resource pricing locked):
 *    chat            $0.0075   → 75 units
 *    email           $0.0075   → 75 units
 *    knowledge       $0.020    → 200 units
 *    scrape          $0.025    → 250 units
 *    report          $0.040    → 400 units
 */
export const TEXT_BUNDLE_RATE_UNITS: Record<TextBundleResource, number> = {
  chat_conversations: 75,
  email_threads: 75,
  knowledge_summaries: 200,
  web_scrapes: 250,
  reports: 400,
};

export type TextBundleResource =
  | 'chat_conversations'
  | 'email_threads'
  | 'knowledge_summaries'
  | 'web_scrapes'
  | 'reports';

export const TEXT_BUNDLE_RESOURCES: TextBundleResource[] = [
  'chat_conversations',
  'email_threads',
  'knowledge_summaries',
  'web_scrapes',
  'reports',
];

/** Total $5.00 expressed in TEN-THOUSANDTHS-OF-A-DOLLAR units. */
export const TEXT_BUNDLE_TOTAL_UNITS = 50000;

/** Display labels for the 5 Text Bundle resources, plus the
 *  human-readable per-unit rate string for the allocation form. */
export const TEXT_BUNDLE_DISPLAY: Record<
  TextBundleResource,
  { label: string; perUnitLabel: string; max: number }
> = {
  chat_conversations: { label: 'Chats', perUnitLabel: '$0.0075', max: 666 },
  email_threads: { label: 'Emails', perUnitLabel: '$0.0075', max: 666 },
  knowledge_summaries: { label: 'Knowledge summaries', perUnitLabel: '$0.020', max: 250 },
  web_scrapes: { label: 'Web scrapes', perUnitLabel: '$0.025', max: 200 },
  reports: { label: 'Reports', perUnitLabel: '$0.040', max: 125 },
};

/** Voice Bundle locked at $7 = 80 min ($0.0875/min) per §A.3. */
export const VOICE_BUNDLE_MINUTES = 80;
export const VOICE_BUNDLE_TOTAL_USD = 7;

/** LiveAvatar Bundle locked at $25 = 50 min ($0.50/min) per §A.3b.
 *  Fail-closed when exhausted — never auto-overage. */
export const LIVEAVATAR_BUNDLE_MINUTES = 50;
export const LIVEAVATAR_BUNDLE_TOTAL_USD = 25;

// ---------- pooled Text top-up (Plan-TextVoicePoolBilling-2026-08 §D) ----------

/** Text Top-Up: $10 for 2,000,000 AI text tokens, FLAT. Deliberately not
 *  a `Record<resource, …>` like TEXT_BUNDLE_RATE_UNITS — the whole point
 *  of the pooled SKU is that there is nothing to allocate. */
export const TEXT_TOPUP_TOKENS = 2_000_000;
export const TEXT_TOPUP_TOTAL_USD = 10;

/** Single point of truth for the top-up endpoint path. VERIFIED against the
 *  shipped backend route — `@router.post("/bundles/text-topup")` in
 *  `back/api/billing_bundles.py`, mounted under `/api/billing`.
 *
 *  🚨 That route 404s while `TEXT_POOL_BILLING_ENABLED` is off, deliberately:
 *  the SKU is meant to be invisible, not merely un-buyable, while dark. The
 *  modal's 404 branch already toasts "not yet available", so a flag-off
 *  workspace that somehow reaches the button gets the honest message rather
 *  than a Stripe error. Do NOT "fix" that 404 into a hard failure. */
export const TEXT_TOPUP_ENDPOINT = '/api/billing/bundles/text-topup';

/** Purchase-history endpoint. VERIFIED against Storm's shipped route
 *  (`back 79d3c11`), owner + admin, 404 while the flag is dark. */
export const TEXT_TOPUP_HISTORY_ENDPOINT = '/api/billing/bundles/text-topups';

/** One purchased top-up. A receipt, not a credit row — top-ups never enter
 *  `workspace_credits`. */
export interface TextTopUpPurchase {
  id: string;
  purchased_at: string;
  tokens_granted: number;
  /** 🚨 Per-purchase remaining IS tracked by the backend — it is not derived
   *  here and must never be computed FE-side. Rows reaching 0 are still
   *  returned (a spent top-up is still a receipt). */
  tokens_remaining: number;
  /** Precomputed by the backend so no caller subtracts; never negative. */
  tokens_consumed: number;
  /** 🚨 MAJOR units — DOLLARS, not cents. `10.0` is ten dollars. The invoice
   *  ledger (`billing_invoices`) uses MINOR units; this endpoint does not,
   *  which is why the field carries its unit in its name and is never a bare
   *  `amount`. Formatting this with the ledger's helper renders $10.00 as
   *  $0.10. */
  amount_paid_usd: number;
  /** Stamped per row from the actual Stripe charge ("usd" today). */
  currency?: string;
  stripe_payment_intent?: string;
  /** 🚨 ALWAYS an explicit `null`, never omitted — deliberately, so that "no
   *  expiry" is something the backend SAYS rather than something the UI
   *  infers from a missing key. Top-ups never expire; if this is ever a
   *  string, something has regressed against the owner decision. */
  expires_at: string | null;
}

/** Response of `TEXT_TOPUP_HISTORY_ENDPOINT`. Purchases are newest-first.
 *
 *  🚨 `balance_tokens` and `UsageSnapshot.text_tokens_topup` are the SAME
 *  number from the SAME source. Never render one figure from one and a second
 *  figure from the other, and never recompute either from the purchase rows —
 *  two derivations of one balance is how a billing page ends up showing two
 *  different remaining balances. If they ever disagree that is a backend bug,
 *  not something to paper over. */
export interface TextTopUpHistoryResponse {
  /** 🚨 Aggregated SERVER-SIDE over EVERY row, never over the returned page.
   *  Summing `purchases[]` to get these is correct only on page one of a
   *  short list and fails DOWNWARD — understating money the customer still
   *  has, on a billing page. Always read the fields. */
  balance_tokens: number;
  total_purchased_tokens: number;
  total_consumed_tokens: number;
  total_paid_usd: number;
  currency?: string;
  /** Paging. The list is unbounded now that nothing expires, so a long-lived
   *  workspace WILL eventually exceed one page. `limit` defaults to 50 and
   *  caps at 200; the backend 400s outside [1,200]. */
  total_count?: number;
  limit?: number;
  offset?: number;
  has_more?: boolean;
  purchases: TextTopUpPurchase[];
}

/** 403 slug from `POST /bundles/text-topup` — the workspace has no active
 *  paid plan. Eligible is `plan ∈ {starter,pro,business,enterprise}` AND
 *  `subscription_status === "active"`.
 *
 *  🚨 `trialing` is REFUSED under the current backend, so a trialing
 *  workspace that hits its wall cannot buy its way out. That is flagged to
 *  the owner and may reverse; if it does it is a backend tuple change and
 *  nothing here, so do NOT build a trial-specific branch against it. */
export const TOPUP_PLAN_REQUIRED_CODE = 'plan_required_for_topup';

/** Pool-consumption thresholds, in percent, at which the customer is
 *  warned. Mirrors the backend alert ladder (Storm's 80 / 95 / 100
 *  `notify_workspace_roles` + owner email) so the bar changes colour on
 *  the SAME boundary that sends the notification — a bar that still reads
 *  green next to an email saying "you are at 80%" is a support ticket.
 *
 *  🚨 If Storm's ladder moves, move these WITH it. */
export const TEXT_POOL_WARN_PCT = 80;
export const TEXT_POOL_CRITICAL_PCT = 95;
export const TEXT_POOL_EXHAUSTED_PCT = 100;

export interface TextBundleCheckoutRequest {
  workspace_id: string;
  allocation: Partial<Record<TextBundleResource, number>>;
}

export interface SimpleBundleCheckoutRequest {
  workspace_id: string;
}

/** Response shape for ALL bundle checkout endpoints — mirror the
 *  existing `createCheckoutSession` shape so the redirect path is
 *  consistent. Final binding shape locked by Storm's Phase 2 (#10) DM;
 *  this type may need a tiny adjustment once that lands. */
export interface BundleCheckoutResponse {
  checkout_url: string;
  workspace_bundle_purchase_id?: string;
}

export type BillingInterval = 'month' | 'year';

export interface ChangePlanRequest {
  workspace_id: string;
  target_plan: Plan;
  billing_interval?: BillingInterval;
}

export interface PreviewChangeRequest {
  workspace_id: string;
  target_plan: Plan;
  billing_interval?: BillingInterval;
}

export interface ProrationPreview {
  target_plan: Plan;
  amount_due: number;
  currency: string;
  period_end: string;
  immediate: boolean;
  lines?: Array<{
    description: string;
    amount: number;
    proration: boolean;
  }>;
  // Backend stub flag: true when the workspace has no active Stripe
  // subscription and the "preview" is really the shape of a first-time
  // Checkout rather than a proration calculation.
  is_trial_upgrade?: boolean;
}

export interface ChangePlanResponse {
  success: boolean;
  pending: boolean;
  status?: 'upgraded' | 'interval_switched' | 'scheduled';
  billing_interval?: BillingInterval;
  effective_at?: string;
  checkout_url?: string;
  message?: string;
}

export interface DowngradeGuardViolation {
  resource: 'agents' | 'articles' | 'team_members';
  current: number;
  allowed: number;
  message: string;
}

export interface SwapRequest {
  direction: 'voice_to_chat' | 'chat_to_voice' | 'voice_to_email' | 'email_to_voice';
  amount: number;
}

/** FE-canonical (`USAGE_METRICS[].key` / UsageSnapshot keys) → the backend's
 *  `overridden_keys` vocabulary (Storm's contract, `f12affe`). The two diverge
 *  on email (FE `_threads` vs backend `_replies`) and knowledge (FE
 *  `_summaries` vs backend `_sources`); everything else is identity-mapped.
 *
 *  Lives here rather than in `usageBars.tsx` (which re-exports it) so that
 *  `checkPlanCardLimitDrift` below can share the one copy without the types
 *  module importing a component module. */
export const OVERRIDE_KEY_REMAP: Record<string, string> = {
  email_threads: 'email_replies',
  knowledge_summaries: 'knowledge_sources',
};

export const overrideKeyFor = (uiKey: string): string =>
  OVERRIDE_KEY_REMAP[uiKey] ?? uiKey;

// ---------- plan comparison cards ----------
//
// 🚨🚨 THE SOURCE OF TRUTH FOR EVERY NUMBER IN `limits` AND `textTokens` IS
// `back/config/plans.py::USAGE_LIMITS`. It is mirrored here, not derived: the
// billing payload only ever carries `usage_limits` for the workspace's CURRENT
// plan, so a four-plan comparison grid has no data source short of a new
// backend endpoint. Mirroring is a deliberate trade, and it has ONE failure
// mode — someone edits `plans.py` and not this file.
//
// 🚨 That edit ships silently, because `main.py` RE-SEEDS `db.plan_limits`
// from `config/plans.py` on EVERY BOOT. So the Python file is not merely the
// documentation of the limits, it is the deployed value; a hand-edit to the DB
// reverts, and a change there is live the moment the backend restarts while
// these cards keep advertising the old figure. Anyone touching `plans.py`
// MUST touch this block in the same change.
//
// The mirror is not left to discipline alone: `checkPlanCardLimitDrift()`
// below compares this table against the live payload for whichever plan the
// workspace is actually on, every time the Billing page renders in dev. It
// cannot cover all four plans at once (only one is ever in the payload), but
// across the team's workspaces it covers them in practice, and it turns a
// silent mismatch into a console error naming the exact key.
export type PlanCardLimitKey =
  | 'max_agents'
  | 'max_articles'
  | 'max_voice_minutes'
  | 'max_team_members'
  | 'max_knowledge_summaries'
  | 'max_video_analyses'
  | 'max_scrapes'
  | 'max_reports';

export interface PlanCardLimitRow {
  key: PlanCardLimitKey;
  label: string;
  /** true → always on the card face. false → behind the grid's shared
   *  "all limits" toggle. */
  primary: boolean;
  /** true → this row is NOT a cap under pooled billing, so the card must not
   *  advertise it there. Hidden when `isPooledBilling(billing)`, kept
   *  otherwise. See the verdict table above. */
  poolGated?: boolean;
}

/** 🚨 EVERY key mirrored from `plans.py`, for `checkPlanCardLimitDrift` ONLY.
 *
 *  Deliberately SEPARATE from `PLAN_CARD_LIMITS` below. The drift check used to
 *  iterate the display array, which coupled two unrelated things: dropping a
 *  row from the CARDS silently dropped its DRIFT COVERAGE too, so the mirror
 *  could rot on exactly the keys we had stopped looking at — fewer rows, a
 *  greener check, and less coverage. `PLAN_DISPLAY[...].limits` still mirrors
 *  all eight numbers from `plans.py`, so all eight are still compared here
 *  whatever the cards choose to show. */
export const PLAN_MIRRORED_LIMIT_KEYS: PlanCardLimitKey[] = [
  'max_agents',
  'max_articles',
  'max_voice_minutes',
  'max_team_members',
  'max_knowledge_summaries',
  'max_video_analyses',
  'max_scrapes',
  'max_reports',
];

/** Row order and face/expander split for the comparison cards.
 *
 *  🚨 CHAT CONVERSATIONS AND EMAIL THREADS ARE ABSENT ON PURPOSE. Under pooled
 *  billing they are NOT caps — they are informational counts drawn from the one
 *  text allowance, which is exactly how `TEXT_POOL_SURFACES` renders them in the
 *  usage view. Putting `max_chat_conversations` on a card would advertise a
 *  limit nothing enforces, and would contradict the usage bars on the same page.
 *
 *  🚨 The expander list SHRANK on 2026-08-28 against a table Storm produced by
 *  EXECUTING the limit function (cap 1, usage 999) rather than reading it —
 *  which matters, because reading lies here: `check_usage_limit`'s hard-block
 *  tuple still CONTAINS `max_chat_conversations` and `max_video_analyses`, but
 *  every live caller branches to the token-pool check before reaching it.
 *  Verdicts: agents / articles / team members / voice minutes are real caps
 *  (403 / 403 / 402 / never pooled); video analyses is pool-funded; knowledge
 *  summaries, web scrapes and reports refuse nowhere at all.
 *
 *  The face rows are the ones that decide whether a customer can SET THE
 *  PRODUCT UP at all — how many assistants they can build, how much help-center
 *  content they can publish, how many people can log in — plus voice, the only
 *  other separately-metered spend. The expander rows are all work VOLUMES that
 *  only start to matter once the workspace is running; none of them is a reason
 *  to choose one plan over another at signup, and four extra rows on the face
 *  would bury the token headline that this grid now leads with. */
export const PLAN_CARD_LIMITS: PlanCardLimitRow[] = [
  { key: 'max_agents', label: 'AI agents', primary: true },
  { key: 'max_articles', label: 'Help center articles', primary: true },
  { key: 'max_voice_minutes', label: 'Voice minutes', primary: true },
  { key: 'max_team_members', label: 'Team members', primary: true },
  // Pool-funded: the only `max_video_analyses` call site branches to
  // `check_text_pool` before the hard-block tuple is ever reached, and
  // `max_knowledge_summaries` has no refusal site at all. Both are dead
  // BECAUSE of pooled billing, so the pool flag is the honest condition.
  { key: 'max_knowledge_summaries', label: 'Knowledge summaries', primary: false, poolGated: true },
  { key: 'max_video_analyses', label: 'Video analyses', primary: false, poolGated: true },
  // 🚨 `max_scrapes` and `max_reports` are GONE, unconditionally. Not a
  // billing-mode question: `track_web_scrape` and `track_report` have ZERO
  // callers, so nothing counts them and nothing refuses on them in any mode.
  // They remain in `PLAN_MIRRORED_LIMIT_KEYS` so the mirror is still checked.
];

export interface PlanDisplayEntry {
  name: string;
  price: string;
  priceNum: number | null;
  tagline: string;
  yearlyPrice: string;
  yearlyPriceNum: number | null;
  yearlyMonthlyEquivalent: string;
  maxAgents: number; // -1 = unlimited
  /** The pooled AI text allowance per cycle, in effective tokens. -1 =
   *  unlimited. This is the headline figure on the card — it is the thing the
   *  customer is actually buying, and it was absent from these cards entirely
   *  until 2026-08-17. */
  textTokens: number;
  /** The remaining GENUINE caps. -1 = unlimited.
   *
   *  🚨 `max_voice_minutes` was RE-CUT on 2026-08-17 (owner-approved margin
   *  change, `back fd2c563`): trial 30→20, starter 60→20, pro 250→80,
   *  business 800→300, enterprise unchanged at -1. The old figures were
   *  pre-repricing legacy values and put margin at cap at 77.1 / 75.2 / 75.9%;
   *  the new ones put it at 81.84 / 81.69 / 81.53%. This is exactly the kind
   *  of backend-side edit `checkPlanCardLimitDrift` exists to catch — it did.
   *
   *  🚨 The Shopify voice figures are NOT these and were deliberately left
   *  alone by that re-cut — see the note under this table. */
  limits: Record<PlanCardLimitKey, number>;
}

export const PLAN_DISPLAY: Record<Plan, PlanDisplayEntry> = {
  // Plan-RemoveFreeTrial.md — the `trial` plan is no longer offered to new
  // signups and is filtered out of every displayed grid. This entry is kept
  // only so existing trialing workspaces' plan label still resolves; the
  // tagline is neutralized (no "free trial" wording).
  // 🚨 `textTokens: 0` is the real `plans.py` value, not a placeholder: the
  // legacy trial tier has NO text allowance. It never reaches a card because
  // `trial` is filtered out of every displayed grid, but the drift check will
  // compare it on a trialing workspace, so it must be honest.
  trial: {
    name: 'Trial', price: '$0', priceNum: 0, tagline: 'Legacy plan',
    yearlyPrice: '$0', yearlyPriceNum: 0, yearlyMonthlyEquivalent: '$0',
    maxAgents: 10, textTokens: 0,
    limits: {
      max_agents: 10, max_articles: 50, max_voice_minutes: 20,
      max_team_members: 1, max_knowledge_summaries: 10,
      max_video_analyses: 2, max_scrapes: 5, max_reports: 3,
    },
  },
  starter: {
    name: 'Starter', price: '$19', priceNum: 19, tagline: 'For solo founders',
    yearlyPrice: '$190', yearlyPriceNum: 190, yearlyMonthlyEquivalent: '$15.83',
    maxAgents: 10, textTokens: 6_000_000,
    limits: {
      max_agents: 10, max_articles: 50, max_voice_minutes: 20,
      max_team_members: 1, max_knowledge_summaries: 20,
      max_video_analyses: 4, max_scrapes: 10, max_reports: 15,
    },
  },
  pro: {
    name: 'Pro', price: '$59', priceNum: 59, tagline: 'Growing teams',
    yearlyPrice: '$590', yearlyPriceNum: 590, yearlyMonthlyEquivalent: '$49.17',
    maxAgents: 30, textTokens: 18_000_000,
    limits: {
      max_agents: 30, max_articles: 300, max_voice_minutes: 80,
      max_team_members: 3, max_knowledge_summaries: 50,
      max_video_analyses: 10, max_scrapes: 25, max_reports: 75,
    },
  },
  business: {
    name: 'Business', price: '$199', priceNum: 199, tagline: 'Scaling orgs',
    yearlyPrice: '$1990', yearlyPriceNum: 1990, yearlyMonthlyEquivalent: '$165.83',
    maxAgents: -1, textTokens: 60_000_000,
    limits: {
      max_agents: -1, max_articles: -1, max_voice_minutes: 300,
      max_team_members: 5, max_knowledge_summaries: 200,
      max_video_analyses: 30, max_scrapes: 100, max_reports: 200,
    },
  },
  enterprise: {
    name: 'Enterprise', price: 'Custom', priceNum: null, tagline: 'Unlimited scale',
    yearlyPrice: 'Custom', yearlyPriceNum: null, yearlyMonthlyEquivalent: 'Custom',
    maxAgents: -1, textTokens: -1,
    limits: {
      max_agents: -1, max_articles: -1, max_voice_minutes: -1,
      max_team_members: -1, max_knowledge_summaries: -1,
      max_video_analyses: -1, max_scrapes: -1, max_reports: -1,
    },
  },
};

/** 🚨 Shopify plans are NOT covered by this table and must never be pointed at
 *  it. `ShopifyBillingView` keeps its own `SHOPIFY_PLANS` copy because the
 *  Shopify tiers are genuinely different products — Shopify Business runs 48M
 *  tokens against Stripe Business's 60M, because the same $199 also bundles
 *  hard-cost HeyGen LiveAvatar minutes. Unifying the two would not remove a
 *  duplicate, it would state one plan's numbers on the other's card.
 *
 *  🚨 VOICE IS NO LONGER ONE OF THE DIFFERENCES — CHECK BEFORE YOU CLAIM IT IS.
 *  Shopify Business and Stripe Business are now EQUAL at 300 voice minutes
 *  (`SHOPIFY_BUSINESS_LIMIT_OVERRIDES` 400 → 300, `back 1de56d6`, owner
 *  2026-08-17). The differences that remain are text (48M vs 60M), chat
 *  (5,000 vs 7,500) and the 60 bundled LiveAvatar minutes Stripe sells as a
 *  separate add-on.
 *
 *  ⚠️ This one line has now been wrong in BOTH directions inside a single day:
 *  it read 200-vs-800 (Shopify tighter), then 400-vs-300 (Shopify looser after
 *  the Stripe re-cut), and is now equal. Voice moved twice on 2026-08-17 and
 *  each move invalidated the prose beside the numbers rather than the numbers
 *  themselves — which is exactly the failure a comment cannot self-detect. Read
 *  `plans.py` before restating this relationship.
 *
 *  🚨 And do not reach for voice again as a margin lever on Shopify Business:
 *  at 48M / 300 / 60 the plan sits at 69.5%, and the 400 → 300 cut bought 1.1
 *  points, not 20. LiveAvatar is 49% of its COGS (one avatar minute costs about
 *  what 22 voice minutes do) and is the deferred lever — owner: "We adjust the
 *  Live avatar in the future." Holding voice at 300, exactly 80.0% needs avatar
 *  at 18 min (20 min gives 79.5%, 30 min gives 77.0%). */

/** Dev-only drift detector for the mirrored table above. Compares the card
 *  numbers for the workspace's CURRENT plan against the limits the backend
 *  actually sent, and returns the keys that disagree.
 *
 *  🚨 Admin-overridden limits are EXCLUDED, not reported. A workspace whose
 *  limits the Prodagi team has raised by hand legitimately differs from its
 *  plan's catalogue figure — that is what `overridden_keys` means, and flagging
 *  it would make the instrument cry wolf on exactly the workspaces most likely
 *  to be open in front of a developer. `overridden_keys` speaks the backend's
 *  bare-resource vocabulary (`knowledge_sources`, not `max_knowledge_summaries`),
 *  hence the strip + `OVERRIDE_KEY_REMAP` round-trip.
 *
 *  🚨 THIS DOES NOT COVER THE SHOPIFY CARDS, AND MUST NOT BE POINTED AT THEM.
 *  It is called only from `StripeBillingPage`, which `IS_SHOPIFY_BUILD`
 *  tree-shakes out of the Shopify bundle, so it never sees a Shopify payload —
 *  and that is correct, because a Shopify payload carries
 *  `SHOPIFY_BUSINESS_LIMIT_OVERRIDES` (48M text / 5,000 chat) while this table
 *  describes the Stripe tiers (60M / 7,500). Calling it there would report
 *  drift on every Shopify Business workspace, for figures that are meant to
 *  differ.
 *
 *  ⚠️ CONSEQUENCE — THE SHOPIFY CARD SET IS UNINSTRUMENTED. The 2026-08-17
 *  voice moves proved it: the Stripe side was caught by this check, the Shopify
 *  side went stale for a day and was only found because a human went looking.
 *  It cannot be closed by reusing this function, because `SHOPIFY_PLANS` holds
 *  its allowances as free-text `features: string[]` ("300 voice minutes / mo"),
 *  and a checker would have to parse prose — the wrong kind of instrument.
 *  The structural fix is to give `SHOPIFY_PLANS` typed numeric fields and
 *  render those strings FROM them, after which a sibling check can compare
 *  numbers against the payload. Not done here: it restructures a card set with
 *  an adjacent live pricing question, so it needs to be its own change.
 *
 *  Returns [] when there is nothing to compare (no plan, no limits payload). */
export const checkPlanCardLimitDrift = (
  plan: Plan | null | undefined,
  limits: PlanLimits | null | undefined,
  overriddenKeys?: string[],
): Array<{ key: string; card: number; server: number }> => {
  if (!plan || !limits) return [];
  const entry = PLAN_DISPLAY[plan];
  if (!entry) return [];
  const overridden = new Set(overriddenKeys ?? []);
  const isOverridden = (limitKey: string) => {
    const bare = limitKey.replace(/^max_/, '');
    return overridden.has(bare) || overridden.has(OVERRIDE_KEY_REMAP[bare] ?? bare);
  };

  const out: Array<{ key: string; card: number; server: number }> = [];
  const compare = (key: string, card: number, server: number | undefined) => {
    // 🚨 `undefined` is NOT a mismatch. `max_video_analyses` and
    // `max_text_tokens` are both legitimately absent from older payloads and
    // from a flag-off backend; treating absence as a disagreement would fire
    // the drift alarm on a perfectly healthy pre-pool deploy.
    if (typeof server !== 'number') return;
    if (isOverridden(key)) return;
    if (server !== card) out.push({ key, card, server });
  };

  compare('max_text_tokens', entry.textTokens, limits.max_text_tokens);
  // 🚨 `PLAN_MIRRORED_LIMIT_KEYS`, NOT `PLAN_CARD_LIMITS` — drift coverage must
  // not shrink just because a row left the cards. See that constant's note.
  for (const key of PLAN_MIRRORED_LIMIT_KEYS) {
    compare(key, entry.limits[key], limits[key]);
  }
  return out;
};

/** -1 → "Unlimited"; everything else localized. 🚨 A raw `-1` must never reach
 *  a card — it reads as an error, and on a pricing page it reads as OUR error. */
export const formatPlanLimit = (n: number): string =>
  n < 0 ? 'Unlimited' : n.toLocaleString();

export const ENTERPRISE_MAILTO = `${SUPPORT_MAILTO}?subject=Enterprise%20plan%20inquiry`;

export const USAGE_METRICS: Array<{
  key: keyof UsageSnapshot;
  limitKey: keyof PlanLimits;
  label: string;
  unit: string;
}> = [
  { key: 'chat_conversations', limitKey: 'max_chat_conversations', label: 'Chat Conversations', unit: 'convos' },
  { key: 'email_threads', limitKey: 'max_email_threads', label: 'Email Threads', unit: 'threads' },
  { key: 'voice_minutes', limitKey: 'max_voice_minutes', label: 'Voice Minutes', unit: 'min' },
  { key: 'knowledge_summaries', limitKey: 'max_knowledge_summaries', label: 'Knowledge Summaries', unit: 'summaries' },
  { key: 'web_scrapes', limitKey: 'max_scrapes', label: 'Web Scrapes', unit: 'scrapes' },
  { key: 'reports', limitKey: 'max_reports', label: 'Reports', unit: 'reports' },
];
